import { useState } from 'react';
import { Button } from '~/components/buttons';
import { FormDialog } from '~/components/dialogs/FormDialog';
import { AvailabilityCalendarPicker } from './AvailabilityCalendarPicker';
import {
  earliestDateOptions,
  isStartDateOnly,
  optionLabel,
  selectionOptionKey,
} from './availabilityOptionUtils';
import type {
  ProviderServiceListingSearchItem,
  PublicAvailabilitySelectionOption,
} from './schemas';

export const localDate = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** The default End date for a window starting on `start` (an ISO date): thirty days later. */
export const defaultEndDate = (start: string) => {
  const end = new Date(`${start}T00:00:00`);
  end.setDate(end.getDate() + 30);
  return localDate(end);
};

export const initialAvailabilityWindow = () => {
  const start = localDate(new Date());
  return { start, end: defaultEndDate(start) };
};

/**
 * The two range rules the availability search API enforces (end on or after
 * start, at most 366 inclusive days), so a bad window is explained before the
 * request instead of coming back as a generic 400. ISO dates compare as text.
 */
export const availabilityWindowError = (start: string, end: string) => {
  if (end < start) return 'Choose an end date on or after the start date.';
  if (Date.parse(end) - Date.parse(start) > 365 * 86_400_000)
    return 'Choose a date range of at most 366 days.';
  return null;
};

export { selectionOptionKey };

export const selectedAvailabilityEntryIds = (
  listing: ProviderServiceListingSearchItem,
  selectedKeys: ReadonlySet<string>
) =>
  [
    ...new Set(
      (listing.availability?.selectionOptions ?? [])
        .filter((option) => selectedKeys.has(selectionOptionKey(option)))
        .flatMap((option) => option.availabilityEntryIds)
    ),
  ].sort((left, right) => left - right);

/** Maximum visible dates and option rows on a provider search result card. */
export const CARD_DATE_LIMIT = 6;

const OptionList = ({
  options,
  selectedKeys,
  onToggle,
}: {
  options: readonly PublicAvailabilitySelectionOption[];
  selectedKeys: ReadonlySet<string>;
  onToggle: (option: PublicAvailabilitySelectionOption) => void;
}) =>
  options.map((option) => {
    const key = selectionOptionKey(option);
    return (
      <label
        className="flex cursor-pointer gap-3 rounded-md border border-grey-900 p-3 text-sm text-content-secondary hover:border-primary-t-600"
        key={key}
      >
        <input
          checked={selectedKeys.has(key)}
          className="mt-0.5"
          type="checkbox"
          onChange={() => onToggle(option)}
        />
        <span>{optionLabel(option)}</span>
      </label>
    );
  });

/**
 * [MCV-1837] The required choice for a part-time/full-time listing whose earliest date has several
 * options: just those options as radios, no calendar. Shows nothing when there is no choice to make.
 */
export const StartOptionChoice = ({
  listing,
  selectedKeys,
  onSelect,
  disabled = false,
}: {
  listing: ProviderServiceListingSearchItem;
  selectedKeys: ReadonlySet<string>;
  onSelect: (option: PublicAvailabilitySelectionOption) => void;
  disabled?: boolean;
}) => {
  const options = earliestDateOptions(listing.availability?.selectionOptions ?? []);
  if (options.length < 2) return null;
  const groupName = `start-option-${listing.providerServiceListingId}`;
  return (
    <fieldset disabled={disabled} className="mt-3 space-y-2">
      <legend className="text-sm font-semibold text-content-primary">Choose a start option</legend>
      {options.map((option) => {
        const key = selectionOptionKey(option);
        return (
          <label
            className="flex cursor-pointer gap-3 rounded-md border border-grey-900 p-3 text-sm text-content-secondary hover:border-primary-t-600"
            key={key}
          >
            <input
              checked={selectedKeys.has(key)}
              className="mt-0.5"
              name={groupName}
              type="radio"
              onChange={() => onSelect(option)}
            />
            <span>{optionLabel(option)}</span>
          </label>
        );
      })}
    </fieldset>
  );
};

export const ProviderAvailabilityOptions = ({
  listing,
  selectedKeys,
  onToggle,
  disabled = false,
  previewLimit,
  previewMode = 'options',
  viewAllMode = 'dialog',
  desktopDialog = false,
}: {
  listing: ProviderServiceListingSearchItem;
  selectedKeys: ReadonlySet<string>;
  onToggle: (option: PublicAvailabilitySelectionOption) => void;
  disabled?: boolean;
  previewLimit?: number;
  previewMode?: 'dates' | 'options';
  viewAllMode?: 'dialog' | 'inline';
  desktopDialog?: boolean;
}) => {
  const [showAll, setShowAll] = useState(false);
  const allOptions = listing.availability?.selectionOptions ?? [];
  // Part-time and full-time providers are hired from a start date, so every
  // Begin path offers only their earliest available date.
  const options = isStartDateOnly(listing.listingKind)
    ? earliestDateOptions(allOptions)
    : allOptions;
  const dates = [...new Set(options.map((option) => option.availabilityDate))];
  const visible: PublicAvailabilitySelectionOption[] = [];
  const hidden: PublicAvailabilitySelectionOption[] = [];
  const previewedDates = new Set<string>();
  for (const option of options) {
    // Search cards preview one option per date; Service Begin keeps its prior
    // first-ten-options behavior. Every hidden option remains in View all.
    if (
      previewLimit === undefined ||
      (visible.length < previewLimit &&
        (previewMode === 'options' || !previewedDates.has(option.availabilityDate)))
    ) {
      visible.push(option);
      previewedDates.add(option.availabilityDate);
    } else {
      hidden.push(option);
    }
  }
  const hiddenSelected = hidden.filter((option) => selectedKeys.has(selectionOptionKey(option)));
  const hiddenSelectedDates = new Set(hiddenSelected.map((option) => option.availabilityDate)).size;
  const visibleDates = new Set(visible.map((option) => option.availabilityDate));
  const hiddenOptionsOnVisibleDates = hidden.filter((option) =>
    visibleDates.has(option.availabilityDate)
  ).length;
  const hiddenDetails = [
    hiddenOptionsOnVisibleDates
      ? `${hiddenOptionsOnVisibleDates} more ${hiddenOptionsOnVisibleDates === 1 ? 'option' : 'options'}`
      : '',
    hiddenSelectedDates
      ? `${hiddenSelectedDates} ${hiddenSelectedDates === 1 ? 'date' : 'dates'} with hidden selections`
      : '',
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <fieldset
      disabled={disabled}
      className={desktopDialog ? 'mt-4 space-y-2 lg:mt-2' : 'mt-4 space-y-2'}
    >
      <legend
        className={`text-sm font-semibold text-content-primary${desktopDialog ? ' lg:sr-only' : ''}`}
      >
        Choose availability to negotiate
      </legend>
      <div className={desktopDialog ? 'space-y-2 lg:hidden' : 'space-y-2'}>
        {viewAllMode === 'inline' && showAll ? (
          <AvailabilityCalendarPicker
            listingId={listing.providerServiceListingId}
            options={options}
            selectedKeys={selectedKeys}
            onToggle={onToggle}
          />
        ) : (
          <OptionList options={visible} selectedKeys={selectedKeys} onToggle={onToggle} />
        )}
      </div>
      {desktopDialog && (
        <Button
          type="button"
          size="sm"
          colorVariant="outline"
          className="hidden max-w-full items-center justify-center lg:inline-flex"
          label={`Choose dates${selectedKeys.size ? ` (${selectedKeys.size} selected)` : ''}`}
          onClick={() => setShowAll(true)}
        />
      )}
      {(hidden.length > 0 || desktopDialog) && (
        <>
          {hidden.length > 0 && (
            <Button
              type="button"
              size="sm"
              colorVariant="outline"
              label={
                viewAllMode === 'inline' && showAll
                  ? 'Show fewer dates'
                  : `View all ${dates.length} ${dates.length === 1 ? 'date' : 'dates'}${hiddenDetails ? ` (${hiddenDetails})` : ''}`
              }
              cyTag={`provider-search-view-all-${listing.providerServiceListingId}`}
              className={desktopDialog ? 'lg:hidden' : undefined}
              onClick={() => setShowAll((current) => !current)}
            />
          )}
          {viewAllMode === 'dialog' && (
            <FormDialog
              editing={showAll}
              onClose={() => setShowAll(false)}
              title={`${listing.listingTitle}: available dates`}
              width="2xl"
            >
              <fieldset disabled={disabled} className="space-y-2 py-4">
                <legend className="sr-only">Choose availability to negotiate</legend>
                <AvailabilityCalendarPicker
                  listingId={listing.providerServiceListingId}
                  options={options}
                  selectedKeys={selectedKeys}
                  onToggle={onToggle}
                />
              </fieldset>
              <Button type="button" label="Done" onClick={() => setShowAll(false)} />
            </FormDialog>
          )}
        </>
      )}
    </fieldset>
  );
};
